import React from 'react'
import {DataObjectId, TaskColor} from 'yougile-model'
import {DragDataObject} from '@common/drag-data'

export enum SortSource {
  /**
   * Sort by specified column id
   */
  ByColumn = 'by-column',
  /**
   * Sort by custom order (specified in settings.data.companySettings?.order see user-settings.ts for details)
   */
  ByCustomOrder = 'by-custom-order'
}

export type RowContent =
  | React.ReactNode
  | string
  | null
  | undefined
  | number
  | boolean

/**
 * Determines the placement of the add field
 */
export enum AddMode {
  BeforeUndoneTasks = 'before-undone-items',
  AfterUndoneTasks = 'after-undone-items'
}

/**
 * Determines the placement of new item
 */
export enum Placement {
  None = 'none',
  Prepend = 'prepend',
  Append = 'append'
}

/**
 * Describes the configuration of the add field
 */
export interface AddInterfaceConfig {
  enabled: boolean
  mode: AddMode
  /**
   * ID of the item where the add field should be placed
   * Keep it null if you want to place the add field at the root level
   */
  id: NullableId
}

/**
 * Contains the temporary item data (for recently created items)
 */
export interface TemporaryItem {
  id: Id
  name: string
  mode: AddMode
}

export type OnMove = (
  fromListId: DataObjectId | string | null,
  toListId: DataObjectId | string | null,
  /**
   * Dragged item ID
   */
  itemId: DataObjectId | string,
  /**
   * ID of the item that should be used as a reference to determine the new position of the item
   */
  relativeId: DataObjectId | string,
  /**
   * Determines whether the item should be placed before the relative item
   */
  before: boolean
) => void

export interface ListViewProps<T> {
  /**
   * List id is only used for the drag and drop between lists.
   * You can keep it null if you don't need this feature
   */
  id?: NullableId
  /** Array of column configs */
  columns: ColConfig<T>[]
  /**
   * Array of row configs. Each row config should contain the row configuration and data itself.
   */
  rows: RowConfig<T>[]
  /**
   * Array of enabled columns (enabled by columns settings)
   */
  enabledColumnIds?: string[]
  /**
   * Custom sort order (applied only if the sort type is 'by-custom-order')
   * Keep it undefined if you'd wish to keep row-based ordering (e.g. using getAllUserTasksIdsByType from tasks-list/utils.ts)
   */
  order?: Id[]
  /**
   * Optional function that renders a row
   * @param row Current row
   * @param columns Array of column configs
   */
  rowRenderer?: (
    content: RowContent,
    row: RowConfig<T>,
    columns: ColConfig<T>[]
  ) => RowContent
  /**
   * Item click handler
   */
  onItemClick?: (
    item: RowConfig<T>,
    event: React.MouseEvent<HTMLDivElement>
  ) => void
  /**
   * Determines whether the items can be dragged inside the list
   */
  draggable?: boolean
  /**
   * Change order handler
   * TODO: Not is use?
   */
  onChangeOrder?: (order: (DataObjectId | string)[]) => void
  /**
   * Drop handler
   */
  onMoveItem?: OnMove
  /**
   * Sort configuration
   */
  sort?: ListSort
  /**
   * This function is called when the row done status check performed
   * @param row
   */
  doneGetter: (data: T) => boolean
  /**
   * This function is called when the row done status set
   * @param data
   * @param value
   */
  doneSetter: (data: T, value: boolean) => void
  /**
   * Determines whether the item can be dragged
   * @param listId
   * @param rowId
   * @param nestingLevel
   */
  canDrag?: (
    listId: NullableId,
    row: T,
    parentId: NullableId,
    nestingLevel: number
  ) => boolean
  /**
   * User function that determines whether the item can be dropped
   * @param fromListId The list id from which the item is dragged (can be null if the list id is not specified in props)
   * @param toListId The list id to which the item is dragged (can be null if the list id is not specified in props)
   * @param itemId The item id that is dragged
   * @param overId The item id over which the dragged item placed (optional, if null then the dragging target is the list itself)
   * @param before Determines whether the dragged item should be placed before the overId item
   */
  canDrop?: (
    fromListId: NullableId,
    toListId: NullableId,
    itemId: Id,
    overId: NullableId,
    toNestingLevel: number,
    before: boolean
  ) => boolean
  /**
   * Optional drag start handler
   * @param rowId
   */
  onDragStart?: (rowId: DataObjectId | string) => void
  onDragEnd?: (rowId: DataObjectId | string) => void
  onDragOverItem?: (rowId: DataObjectId | string) => void
  onDragEnterItem?: (rowId: DataObjectId | string) => void
  onDragLeaveItem?: (rowId: DataObjectId | string) => void
  /**
   * Optional function that creates drag data on drag start
   * It accepts dragged item id and returns drag data object (DragDataObject)
   * @param rowId
   */
  createDragData?: (rowId: DataObjectId | string) => DragDataObject | undefined
  /**
   * Display drop placeholder at the top of the list
   * It is used for the cases when draggable item from
   * the outside of the list is dragged over the list wrapper
   * @param row
   */
  markAsDropTarget?: Placement
  /**
   * Function that render item menu
   * @param row
   */
  renderItemMenu: (
    element: HTMLElement,
    row: RowConfig<T>,
    parentId: NullableId,
    api: ListStoreExposedApi,
    closeMenu: () => void
  ) => React.ReactNode
  /**
   * Handler that is called when the item name is changed
   * @param id
   * @param newName
   */
  onNameChange?: (id: DataObjectId | string, newName: string) => void
  /**
   * Determines whether the items can be created for the whole list
   * If value is false the add field will not be displayed
   */
  allowCreateItems?: boolean
  /**
   * User can drag items within items sorted by columns
   * TODO: Experimental
   */
  allowDragWithinSortedByColumns?: boolean
  /**
   * Determines whether the children can be created for specific item
   * @param parentId
   */
  canCreateItemsFor?: (parentId: DataObjectId | string | null) => boolean
  /**
   *
   * @param parentId
   * @param name
   * @param placement
   * @param onItemReady Optional callback that is called when the item is created (accepts id)
   */
  onCreateItem: (
    parentId: DataObjectId | string | null,
    name: string,
    placement: Placement,
    onItemIdReady?: (id: Id) => void
  ) => void
  /**
   * Specifies first column view config. Since the first column is fixed and doesn't get its config from the columns array it should be specified separately
   * @param data
   */
  primaryColumnConfig?: ColViewConfig<T>
  /**
   * Function that returns the name of the item
   * @param data
   */
  selectName: (data: T) => string
  /**
   * Optional function that returns the archived status of the item
   * @param data
   */
  selectArchived?: (data: T) => boolean
  /**
   * Optional function that returns the lazy status of the item (the data is not loaded yet)
   * @param data
   */
  selectLazy?: (data: T) => boolean
  /**
   * Optional function that returns the color of the item
   * @param data
   */
  selectColor?: (data: T) => TaskColor
  /**
   * Optional function that should return true if the item can be edited
   * @param data
   */
  selectEditable?: (data: T) => boolean
  /**
   * Optional function that should return true if the item can be deleted
   * @param data
   */
  selectTooltipContent?: (data: T) => string | null
  /**
   * Optional function that only observes the moment when the add field is shown
   * @param id
   */
  onAddEditorShown?: (id: NullableId) => void
  /**
   * Hide the list UI (but you can still interact with the list via API)
   */
  hidden?: boolean
  highlightedItem?: Id,
  className?: string
}

export interface ListViewStoreConfig<T> {
  listId: NullableId
  columns: ListViewProps<T>['columns']
  doneGetter: ListViewProps<T>['doneGetter']
  rowRenderer?: ListViewProps<T>['rowRenderer']
  renderItemMenu: ListViewProps<T>['renderItemMenu']
  primaryColumnConfig?: ListViewProps<T>['primaryColumnConfig']
  allowCreateItems?: ListViewProps<T>['allowCreateItems']
  canCreateItemsFor?: ListViewProps<T>['canCreateItemsFor'],
  highlightedItem?: ListViewProps<T>['highlightedItem']
}

export interface ListStoreExposedApi {
  rename(id: DataObjectId | string): void
  addSubTask(id: NullableId, mode?: AddMode): void
}

export enum SortType {
  Date = 'date',
  Deadline = 'deadline',
  Alphabetical = 'alphabetical',
  Completion = 'completion',
  Favorites = 'favorites'
}

export interface ListSort {
  type: SortSource
  columnId: NullableId
  asc: boolean
}

/**
 * Sort config for the column
 */
export interface SortConfig<TData> {
  icon: string
  title: string
  sortBy?: (data: TData, asc: boolean) => string | number | undefined
  type: SortType
}

export interface ColViewConfig<TData> {
  width?: number | string | 'auto'
  columnClassName?: string
  cellClassName?: string
  renderer?: (row: RowConfig<TData>) => RowContent
  /**
   * Column display name
   */
  fullName: string
  /**
   * React node compatible title
   */
  shortName: React.ReactNode | string
}

export enum ColumnVisiblity {
  /**
   * Column never shown
   */
  AlwaysHidden = 'always-hidden',
  /**
   * Column always shown
   */
  AlwaysVisible = 'always-visible'
}

export interface ColConfig<TData> extends ColViewConfig<TData> {
  /**
   * Column id
   */
  id: string | DataObjectId
  hideOnDrag?: boolean
  sort?: SortConfig<TData>
  /**
   * Determines whether the column is visible or not
   * Skip this property if you want to show the column by default and allow the user to hide it
   */
  visibility?: ColumnVisiblity
  /**
   * Column will be added to the active columns list for new lists by the default
   */
  isDefault?: boolean
}

export interface RowConfig<T> {
  /**
   * Data itself
   */
  data: T
  /**
   * Can be dragged
   */
  draggable?: boolean
  /**
   * Can be dragged outside the list
   */
  draggableOut?: boolean
  /**
   * Data that will be passed to the drop event
   */
  transferData?: DragDataObject
  /**
   * Children rows
   */
  children?: RowConfig<T>[]
}

export type Id = DataObjectId | string
export type NullableId = Id | null

export interface Identifiable {
  id: Id
}

export interface DropPlaceholderState {
  visible: boolean
  nestedLevel: number
  targetElement?: HTMLElement
  isBefore?: boolean
}

export interface CapturedByListEvent {
  capturedByList?: boolean
}
